/**
 * useRouteMutations — every write that changes a route or its rating.
 * Extracted from useMainApp for maintainability.
 *
 * What is here and what is NOT is the useful line. This hook holds the writes
 * that need nothing but gym context: edit, delete, rate, bulk archive, bulk
 * delete. The route PLACEMENT flow (`pending`, the map tap, reposition) stays
 * in `useMainApp` because it is a UI mode, not a write — it owns which tab you
 * are on, what is selected, and which map is showing, and pulling it out would
 * mean handing this hook five setters.
 *
 * Two invariants live in here:
 *
 * - **Every write that flips `active` goes through `archiveStamp`.** See its
 *   own comment for what breaks otherwise.
 * - **Anything that moves a route between the active and archived buckets
 *   drops the archive cache** (`invalidateArchivedRoutes`). That cache is
 *   fetched on demand, so nothing else would notice it had gone stale.
 *
 * Counter bookkeeping is deliberately absent: `activeRoutes` and the
 * per-discipline split are maintained by the `updateRouteBreakdown` trigger,
 * and the delete cascades by their Cloud Functions. A client-side increment
 * here would drift.
 */
import { useCallback } from "react";
import { db } from "../firebase";
import { deleteDoc, setDoc, updateDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { todayStr } from "../utils";
import { useTranslation } from "../i18n";
import { containsProfanity } from "../profanityFilter";
import { trackMAU } from "./useMAU";
import { logEvent, EVT } from "../analytics";
import { useToast } from "../components/ui";
import { callableErrorText } from "../utils/callableError";
import { callFunction } from "../utils/callFunction";

/**
 * Archive-moment stamp for a route write. `active: false` alone carries no time
 * signal, and the monthly market snapshot's route lifecycle stats
 * (functions/marketSnapshot.js) need to know WHEN a route came down.
 * Reactivation clears the field so a later re-archive gets a fresh stamp; a
 * write that does not touch `active` adds nothing.
 *
 * EVERY path that flips `active` must go through this. Bulk archive shipped
 * without the stamp, which left the most common takedown of all — rotating a
 * whole sector — invisible to the lifecycle stats, and to anything else that
 * has to reconstruct which routes were on the wall in a past period.
 * `scripts/backfill-archived-at.js` recovers the missing stamps from the audit
 * log.
 */
const archiveStamp = (active) => active === false ? { archivedAt: serverTimestamp() }
  : active === true ? { archivedAt: null } : {};

export function useRouteMutations({ gymId, gymDoc, authUser, gymMeta, invalidateArchivedRoutes }) {
  const { t } = useTranslation();
  const toast = useToast();
  const uid = authUser?.uid;
  const timezone = gymMeta?.timezone;

  const handleEditRoute = useCallback(async (routeId, updates) => {
    if (!routeId) return;
    await updateDoc(gymDoc("routes", routeId), { ...updates, ...archiveStamp(updates.active), lastModifiedBy: uid || null });
    if (updates.active !== undefined) {
      // activeRoutes / per-discipline counters are maintained server-side by
      // the `updateRouteBreakdown` trigger (fires on this active-toggle in
      // both directions). No fragile client-side fire-and-forget increment.
      // The archive view's on-demand cache is now stale — drop it so the
      // next visit refetches and the moved route disappears (or appears) on
      // the correct side.
      invalidateArchivedRoutes?.();
    }
  }, [gymDoc, uid, invalidateArchivedRoutes]);

  /**
   * Hard-delete a single route and all its associated ascents + ratings.
   * Used by the RouteModal when an archived route is permanently removed.
   *
   * The deleteRoutesCascade CF enforces the routes.delete capability
   * server-side; the caller (RouteModal) must still gate the UI on
   * can('routes.delete'). Counter bookkeeping happens in the CF.
   */
  const handleDeleteRoute = useCallback(async (routeId) => {
    if (!routeId) return;
    // Server-side cascade (route + every user's ascents + ratings +
    // watchers + counters) — see handleBulkDelete for the rationale.
    try {
      await callFunction("deleteRoutesCascade", { gymId, routeIds: [routeId] });
    } catch (e) {
      toast.push({ text: t('common.error') + ": " + callableErrorText(e, e), durationMs: 6000 });
      return;
    }
    // Hard-delete drops the route entirely — invalidate the archive cache so
    // the deleted entry doesn't linger in the next archived-view render.
    invalidateArchivedRoutes?.();
  }, [gymId, invalidateArchivedRoutes, toast, t]);

  /** One tap on the fullscreen map archives (or restores) a route. */
  const setRouteActive = useCallback(async (routeId, active) => {
    await handleEditRoute(routeId, { active });
    // Only the archive direction is an "archive" event; the un-archive half is
    // an undo, and counting it would inflate the funnel.
    if (active === false) {
      logEvent(EVT.ADMIN_ROUTE_ARCHIVE, { gym_id: gymId, batch_size: 1, method: "map_tap" });
    }
  }, [handleEditRoute, gymId]);

  const handleRate = useCallback(async (routeId, stars, comment) => {
    if (!uid) return;
    // Instant client-side pre-filter for obvious profanity — saves a network
    // round-trip and gives immediate feedback. The authoritative moderation
    // runs server-side in the `submitRating` callable.
    if (comment && containsProfanity(comment)) {
      return { error: "profanity", category: "profanity" };
    }
    const hasStars = Number(stars) > 0;
    const hasComment = !!(comment && comment.trim());

    // A rating is meaningful with either a star score or a written comment.
    // A comment-bearing rating MUST go through the moderated `submitRating`
    // callable — Firestore rules block a direct client write of a non-empty
    // comment. A comment-only doc is stored with stars=0; the ratingStats CF
    // normalizes stars<=0 to "absent" so it never skews the average.
    if (hasComment) {
      try {
        await callFunction("submitRating", { gymId, routeId, stars: hasStars ? Number(stars) : 0, comment, updatedAt: todayStr(timezone) });
      } catch (e) {
        // failed-precondition = moderation rejected the comment (with a
        // category); resource-exhausted = per-user rate limit.
        if (e?.code === "functions/failed-precondition") return { error: "profanity", category: e?.details?.category };
        if (e?.code === "functions/resource-exhausted") return { error: "rateLimit" };
        return { error: "save" };
      }
      trackMAU(gymId, uid).catch(() => {});
      logEvent(EVT.ROUTE_RATE, { gym_id: gymId, rating: stars });
      logEvent(EVT.ROUTE_COMMENT, { gym_id: gymId, has_stars: hasStars });
      return { success: true };
    }

    // Star-only or cleared rating — fast direct path (no moderation needed).
    const ratingId = uid + "_" + routeId;
    if (!hasStars) {
      // Both stars and comment empty — the user cleared their rating entirely.
      await deleteDoc(gymDoc("ratings", ratingId)).catch(() => {});
      logEvent(EVT.ROUTE_RATE, { gym_id: gymId, rating: 0 });
      return { success: true };
    }
    const data = { userId: uid, routeId, stars: Number(stars), comment: "", updatedAt: todayStr(timezone) };
    await setDoc(gymDoc("ratings", ratingId), data);
    trackMAU(gymId, uid).catch(() => {});
    logEvent(EVT.ROUTE_RATE, { gym_id: gymId, rating: stars });
    return { success: true };
  }, [uid, gymId, gymDoc, timezone]);

  const handleBulkArchive = async (routeIds) => {
    const batch = writeBatch(db);
    routeIds.forEach(id => batch.update(gymDoc("routes", id), { active: false, ...archiveStamp(false), lastModifiedBy: uid || null }));
    await batch.commit();
    // activeRoutes / per-discipline counters are maintained server-side: each
    // archived route fires the `updateRouteBreakdown` trigger (-1). No
    // client-side fire-and-forget increment.
    // Routes just moved into the archived bucket — drop the cache so the
    // next archived-view fetch picks them up.
    invalidateArchivedRoutes?.();
    logEvent(EVT.ADMIN_ROUTE_ARCHIVE, { gym_id: gymId, batch_size: routeIds.length });
  };

  // Hard-delete routes via the deleteRoutesCascade CF. Server-side because
  // the client's `ascents` listener is user-scoped — a client-side cascade
  // would only see (and delete) the acting admin's own ascents. The CF
  // removes the routes plus ALL related ascents/ratings/watchers; the
  // sharded counters are maintained by the per-document delete triggers.
  const handleBulkDelete = async (routeIds) => {
    try {
      await callFunction("deleteRoutesCascade", { gymId, routeIds });
    } catch (e) {
      toast.push({ text: t('common.error') + ": " + callableErrorText(e, e), durationMs: 6000 });
      return;
    }
    // Bulk-delete may have removed archived routes — invalidate the cache.
    invalidateArchivedRoutes?.();
  };

  return {
    handleEditRoute, handleDeleteRoute, setRouteActive,
    handleRate, handleBulkArchive, handleBulkDelete,
  };
}
